import type { FC, PropsWithChildren } from 'react'
import { createContext, useContext, useEffect } from 'react'

import { useShowWidgetAfterChannelJoin } from '@liveHelper/hooks/useShowWidgetAfterChannelJoin'
import { actionsSelector, useStore } from '@liveHelper/models/store'

import { useCloseWidgetEnabled } from './useCloseWidgetEnabled'

export type CloseWidgetContextProps = {
  canCloseWidget: boolean
  closeWidget: () => void
}

export const CloseWidgetContext = createContext<CloseWidgetContextProps>({
  canCloseWidget: false,
  closeWidget: () => null,
})

/** Provider component that provides the close widget function. */
export const CloseWidgetProvider: FC<PropsWithChildren> = ({ children }) => {
  const { closeWidget, clearCloseWidgetTime, hideWidget } =
    useStore(actionsSelector)
  const showWidgetAfterChannelJoin = useShowWidgetAfterChannelJoin()

  const { isCloseFeatureEnabled, widgetShouldBeVisible, canCloseWidget } =
    useCloseWidgetEnabled()

  useEffect(() => {
    if (!isCloseFeatureEnabled) {
      return
    }

    if (widgetShouldBeVisible) {
      showWidgetAfterChannelJoin()
      clearCloseWidgetTime()
    } else {
      hideWidget()
    }
  }, [
    isCloseFeatureEnabled,
    widgetShouldBeVisible,
    showWidgetAfterChannelJoin,
    clearCloseWidgetTime,
    hideWidget,
  ])

  return (
    <CloseWidgetContext.Provider
      value={{
        canCloseWidget,
        closeWidget,
      }}
    >
      {children}
    </CloseWidgetContext.Provider>
  )
}

/** @returns CloseWidgetContext */
export const useCloseWidgetContext = () => {
  return useContext(CloseWidgetContext)
}
